import React from 'react';
import { Coins } from 'lucide-react';
import UsageBar from './UsageBar';

export interface BalanceCardProps {
  /** Credits available, in WP. */
  wp: number;
  /** The plan's balance cap — the usage-bar denominator. */
  cap: number;
  plan: string;
  /** Next renewal/charge date (from Stripe), or null on a free plan. */
  nextRenewal: Date | null;
  loading?: boolean;
  /** The wallet has never held a credit — a new creator, told what is free rather than that they ran out. */
  neverFunded?: boolean;
}

/**
 * The wallet's headline card: one balance in Wisetree Points, the same quiet usage bar the profile shows, and
 * where more comes from. Presentational — every number arrives as a prop.
 *
 * NOTHING REFRESHES ANY MORE (2026-09-19). This card used to say credits "refresh with your subscription each
 * month" and printed a next-refresh date. Since the subscription became a seller LICENCE it grants no credits
 * at all, so both lines described a top-up that would never arrive — the worst kind of wrong, because a
 * creator waiting for a refresh does not go and buy what they need. The renewal date still exists and still
 * matters, but it is the date the LICENCE renews, so that is what it now says.
 */
const BalanceCard: React.FC<BalanceCardProps> = ({ wp, cap, plan, nextRenewal, loading, neverFunded }) => (
  <section className="mt-6 rounded-2xl border border-secondary-200 dark:border-secondary-700 p-5">
    <p className="text-sm text-secondary-500">Your Wisetree Wallet</p>
    <div className="mt-3 flex items-center gap-3">
      <Coins className="w-6 h-6 text-secondary-500" />
      <div>
        <p className="text-2xl font-bold leading-none" data-testid="wallet-wp">
          {loading ? '…' : `${wp.toLocaleString()} WP`}
        </p>
        <p className="mt-1 text-xs text-secondary-400">Wisetree Points — your credits for everything</p>
      </div>
    </div>

    <div className="mt-4">
      <UsageBar remaining={wp} cap={cap} loading={loading} neverFunded={neverFunded} />
    </div>

    <p className="mt-3 text-xs text-secondary-400">
      Credits come from top-ups, on every plan. They are spent as you generate, and they never expire.
    </p>
    {plan !== 'free' && nextRenewal && (
      <p className="mt-1 text-xs text-secondary-400">
        Your plan renews on{' '}
        <span className="font-medium text-secondary-600 dark:text-secondary-300">
          {nextRenewal.toLocaleDateString(undefined, { day: 'numeric', month: 'long', year: 'numeric' })}
        </span>
        {' '}— that keeps selling switched on; it does not add credits.
      </p>
    )}
  </section>
);

export default BalanceCard;
